<script setup lang="ts">
import SettingsSortedListInput from "./SettingsSortedListInput.vue";
import {
  defaultReportSettings,
  RSFindingsColumns,
  RSFindingsDisplayMode,
  RSImagesCaptionValues,
  RSImagesPerPage,
  RSOverviewsPlacement,
  RSViewsDisplayMode,
  type ReportSettings,
} from "@/types";
import { useReportSettingsController } from "./useUpdateReportSettings.ts";
import { computed } from "vue";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const props = defineProps<{
  templateSettings?: ReportSettings;
}>();

const templateSettings = computed(() => props.templateSettings);
const { settings } = useReportSettingsController(templateSettings);

const displayMode = computed({
  get: () =>
    settings.value?.findingsDisplayMode ??
    defaultReportSettings.findingsDisplayMode,
  set: (val) => {
    if (settings.value) settings.value.findingsDisplayMode = val;
  },
});

const overviewsPlacement = computed({
  get: () =>
    settings.value?.overviewsPlacement ??
    defaultReportSettings.overviewsPlacement,
  set: (val) => {
    if (settings.value) settings.value.overviewsPlacement = val;
  },
});

const viewsDisplayMode = computed({
  get: () =>
    settings.value?.findingsViewsDisplayMode ??
    defaultReportSettings.findingsViewsDisplayMode,
  set: (val) => {
    if (settings.value) settings.value.findingsViewsDisplayMode = val;
  },
});

const imagesPerPage = computed({
  get: () =>
    settings.value?.appendicesImagesPerPage ??
    defaultReportSettings.appendicesImagesPerPage,
  set: (val) => {
    if (settings.value) settings.value.appendicesImagesPerPage = val;
  },
});

const displayModes = [
  {
    value: RSFindingsDisplayMode.TABLE,
    icon: "bi-table",
    titleKey: "dashboard:templates.drawer.settings.displayMode.table.title",
    descKey: "dashboard:templates.drawer.settings.displayMode.table.desc",
  },
  {
    value: RSFindingsDisplayMode.LIST,
    icon: "bi-list-ul",
    titleKey: "dashboard:templates.drawer.settings.displayMode.list.title",
    descKey: "dashboard:templates.drawer.settings.displayMode.list.desc",
  },
  {
    value: RSFindingsDisplayMode.SPLIT_TABLES,
    icon: "bi-postcard",
    titleKey:
      "dashboard:templates.drawer.settings.displayMode.splitTables.title",
    descKey: "dashboard:templates.drawer.settings.displayMode.splitTables.desc",
  },
];

const columnOptions = computed(() =>
  Object.values(RSFindingsColumns).map((col) => ({
    label: t(
      `dashboard:templates.drawer.settings.displayedInfo.columns.${col}`
    ),
    value: col,
  }))
);

const splitTableInfoOptions = computed(() =>
  Object.values(RSFindingsColumns)
    .filter((col) => col !== RSFindingsColumns.IMAGE_NUMBERING)
    .map((col) => ({
      label: t(
        `dashboard:templates.drawer.settings.displayedInfo.columns.${col}`
      ),
      value: col,
    }))
);

const captionOptions = computed(() =>
  Object.values(RSImagesCaptionValues).map((val) => ({
    label: t(`dashboard:templates.drawer.settings.imageCaption.options.${val}`),
    value: val,
  }))
);

const currentInfoKey = computed<keyof ReportSettings>(() => {
  if (displayMode.value === RSFindingsDisplayMode.TABLE)
    return "findingsTableDisplayedColumns";
  if (displayMode.value === RSFindingsDisplayMode.LIST)
    return "findingsListDisplayedInfo";
  return "findingsSplitTablesDisplayedInfo";
});

const currentInfoOptions = computed(() =>
  displayMode.value === RSFindingsDisplayMode.SPLIT_TABLES
    ? splitTableInfoOptions.value
    : columnOptions.value
);
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <!-- Section 1: Display mode -->
    <div class="settings-section">
      <div class="section-title">
        {{ t("dashboard:templates.drawer.settings.displayMode.title") }}
      </div>
      <small class="text-description d-block mb-3">
        {{ t("dashboard:templates.drawer.settings.displayMode.desc") }}
      </small>
      <div class="d-flex flex-column gap-2">
        <div
          v-for="mode in displayModes"
          :key="mode.value"
          class="mode-card"
          :class="{ selected: displayMode === mode.value }"
          @click="displayMode = mode.value"
        >
          <i
            :class="mode.icon"
            class="mode-icon"
            :style="{
              color:
                displayMode === mode.value
                  ? 'var(--bs-primary)'
                  : 'var(--bs-secondary-color)',
            }"
          ></i>
          <div class="flex-grow-1">
            <div class="fw-medium">{{ t(mode.titleKey) }}</div>
            <small class="text-description">{{ t(mode.descKey) }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Displayed info (conditional) -->
    <div class="settings-section">
      <div class="section-title">
        {{ t("dashboard:templates.drawer.settings.displayedInfo.title") }}
      </div>
      <small class="text-description d-block mb-2">
        {{ t("dashboard:templates.drawer.settings.displayedInfo.desc") }}
      </small>
      <SettingsSortedListInput
        :model-value="
          (settings?.[currentInfoKey] as string[]) ??
          (defaultReportSettings?.[currentInfoKey] as string[])
        "
        @update:model-value="(settings[currentInfoKey] as any) = $event"
        :options="currentInfoOptions"
      />
    </div>

    <!-- Section 3: Photographic survey -->
    <div class="settings-section">
      <div class="section-title">
        {{ t("dashboard:templates.drawer.settings.overviews.title") }}
      </div>
      <small class="text-description d-block mb-3">
        {{ t("dashboard:templates.drawer.settings.overviews.desc") }}
      </small>

      <!-- Placement of general views -->
      <div class="mb-3">
        <small class="text-description fw-medium d-block mb-2">
          {{
            t("dashboard:templates.drawer.settings.overviews.placement.label")
          }}
        </small>
        <div class="d-flex flex-column gap-2">
          <div
            class="option-card"
            :class="{
              selected: overviewsPlacement === RSOverviewsPlacement.FINDINGS,
            }"
            @click="overviewsPlacement = RSOverviewsPlacement.FINDINGS"
          >
            <div class="fw-medium">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.placement.findings"
                )
              }}
            </div>
            <small class="text-description">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.placement.findingsDesc"
                )
              }}
            </small>
          </div>
          <div
            class="option-card"
            :class="{
              selected:
                overviewsPlacement === RSOverviewsPlacement.VIEWS_SECTION,
            }"
            @click="overviewsPlacement = RSOverviewsPlacement.VIEWS_SECTION"
          >
            <div class="fw-medium">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.placement.viewsSection"
                )
              }}
            </div>
            <small class="text-description">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.placement.viewsSectionDesc"
                )
              }}
            </small>
          </div>
        </div>
      </div>

      <!-- Views display mode (always visible - controls part views too) -->
      <div class="mb-3">
        <small class="text-description fw-medium d-block mb-2">
          {{
            t("dashboard:templates.drawer.settings.overviews.displayMode.label")
          }}
        </small>
        <div class="d-flex flex-column gap-2">
          <div
            class="option-card"
            :class="{
              selected: viewsDisplayMode === RSViewsDisplayMode.INLINE,
            }"
            @click="viewsDisplayMode = RSViewsDisplayMode.INLINE"
          >
            <div class="fw-medium">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.displayMode.inline"
                )
              }}
            </div>
            <small class="text-description">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.displayMode.inlineDesc"
                )
              }}
            </small>
          </div>
          <div
            class="option-card"
            :class="{
              selected: viewsDisplayMode === RSViewsDisplayMode.REFERENCE,
            }"
            @click="viewsDisplayMode = RSViewsDisplayMode.REFERENCE"
          >
            <div class="fw-medium">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.displayMode.reference"
                )
              }}
            </div>
            <small class="text-description">
              {{
                t(
                  "dashboard:templates.drawer.settings.overviews.displayMode.referenceDesc"
                )
              }}
            </small>
          </div>
        </div>
      </div>

      <small class="text-description d-block">
        {{
          t(
            "dashboard:templates.drawer.settings.overviews.sectionOverviewsNote"
          )
        }}
      </small>
    </div>

    <!-- Section 4: Appendices images per page -->
    <div class="settings-section">
      <div class="section-title">
        {{ t("dashboard:templates.drawer.settings.appendices.title") }}
      </div>
      <div class="d-flex gap-2">
        <div
          class="mode-card flex-grow-1"
          :class="{ selected: imagesPerPage === RSImagesPerPage.TWO }"
          @click="imagesPerPage = RSImagesPerPage.TWO"
        >
          <i
            class="bi-view-stacked mode-icon"
            :style="{
              color:
                imagesPerPage === RSImagesPerPage.TWO
                  ? 'var(--bs-primary)'
                  : 'var(--bs-secondary-color)',
            }"
          ></i>
          <small class="fw-medium">{{
            t("dashboard:templates.drawer.settings.appendices.two")
          }}</small>
        </div>
        <div
          class="mode-card flex-grow-1"
          :class="{ selected: imagesPerPage === RSImagesPerPage.FOUR }"
          @click="imagesPerPage = RSImagesPerPage.FOUR"
        >
          <i
            class="bi-grid mode-icon"
            :style="{
              color:
                imagesPerPage === RSImagesPerPage.FOUR
                  ? 'var(--bs-primary)'
                  : 'var(--bs-secondary-color)',
            }"
          ></i>
          <small class="fw-medium">{{
            t("dashboard:templates.drawer.settings.appendices.four")
          }}</small>
        </div>
      </div>
    </div>

    <!-- Section 5: Image captions -->
    <div class="settings-section">
      <div class="section-title">
        {{ t("dashboard:templates.drawer.settings.imageCaption.title") }}
      </div>
      <small class="text-description d-block mb-2">
        {{ t("dashboard:templates.drawer.settings.imageCaption.desc") }}
      </small>
      <SettingsSortedListInput
        :model-value="
          (settings?.imagesCaptionValues as string[]) ??
          (defaultReportSettings.imagesCaptionValues as string[])
        "
        @update:model-value="(settings.imagesCaptionValues as any) = $event"
        :options="captionOptions"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.settings-section {
  padding-bottom: 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.section-title {
  font-size: 13px;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--bs-primary);
  margin-bottom: 12px;
}

.text-description {
  color: var(--bs-body-color);
  font-size: 12px;
  opacity: 0.7;
}

.mode-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1.5px solid var(--el-border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--el-border-color-dark);
  }

  &.selected {
    border-color: var(--bs-primary);
    background-color: var(--el-color-primary-light-9);
  }
}

.mode-icon {
  font-size: 28px;
  flex-shrink: 0;
  width: 32px;
  text-align: center;
}

.option-card {
  padding: 10px 14px;
  border: 1.5px solid var(--el-border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--el-border-color-dark);
  }

  &.selected {
    border-color: var(--bs-primary);
    background-color: var(--el-color-primary-light-9);
  }
}
</style>
